import {
  Box,
  Center,
  Divider,
  Flex,
  Icon,
  Skeleton,
  SkeletonCircle,
  Spacer,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { useEffect, useState } from 'react'
import { useGetClockStatus, useClockIn, useClockOut } from '../../hooks/useHourRegistrations'
import FormBrandSelect from './forms/FormBrandSelect'
import FormMemberSelect from './forms/FormMemberSelect'
import FormProductTypeSelect from './forms/FormProductTypeSelect'
import FormSelect from './forms/FormSelect'
import { ArrowRightThick, MusicPlay, MusicStop, Plus } from '../../utils/constants/icons'
import LazyLoadAvatar from './media/LazyLoadedAvatar'
import ConfirmModal from './modals/ConfirmModal'
import { useAuth } from '../../hooks/useAuth'

const TRANSITION = '0.2s ease-in'

import { PILLAR_OPTIONS as PILLAR_TYPES, REGION_OPTIONS as REGION_TYPES } from '../../utils/constants/hourRegistrations'

const blinkAnimation = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
`

const formatTime = (): string => {
  return new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  })
}

const formatDate = (): string => {
  return new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const PlayIcon = () => (
  <Icon color="white" width="14px" height="14px" viewBox="0 0 24 24" ml="2px">
    <MusicPlay />
  </Icon>
)

const StopIcon = () => (
  <Icon color="white" width="10px" height="10px" viewBox="0 0 10 10">
    <MusicStop />
  </Icon>
)

const AddMemberButton = ({ onClick }: { onClick: () => void }) => (
  <Tooltip label="Add a member" aria-label="Add a member" hasArrow>
    <Tag w="24px" h="24px" p="0" bg="transparent" onClick={onClick}>
      <Icon w="24px" h="24px" viewBox="0 0 24 24" color="neutral.300" cursor="pointer">
        <Plus />
      </Icon>
    </Tag>
  </Tooltip>
)

interface Member {
  id: string
  name: string
  image: string | undefined | null
}

interface Brand {
  id: string
  name: string
  image: string | undefined | null
}

interface ProductType {
  id: string
  name: string
}

const ClockInClockOut: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(formatTime())
  const [clockedInSeconds, setClockedInSeconds] = useState(0)
  const [isLoadingAction, setIsLoadingAction] = useState(false)
  const [isClockedIn, setIsClockedIn] = useState(false)
  const [member, setMember] = useState<Member | null>(null)
  const [brand, setBrand] = useState<Brand | null>(null)
  const [productType, setProductType] = useState<ProductType | null>(null)
  const [pillar, setPillar] = useState<string>('')
  const [region, setRegion] = useState<string>('')

  const { isOpen: isConfigModalOpen, onOpen: onConfigModalOpen, onClose: onConfigModalClose } = useDisclosure()

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(formatTime())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const { user } = useAuth()

  const clockStatusQuery = useGetClockStatus(user?.id ?? '')
  const { isLoading } = clockStatusQuery

  // Sync clock status data to local state
  useEffect(() => {
    const data = clockStatusQuery.data
    if (data === undefined) return // query hasn't resolved yet

    if (data) {
      setIsClockedIn(true)
      setClockedInSeconds((Date.now() - new Date(data.start_time).getTime()) / 1000)
      setPillar(data.pillar)

      // Set member
      if (data.member) {
        setMember({
          id: data.member.id,
          name: data.member.name,
          image: data.member.logo ?? null,
        })
      } else {
        setMember(null)
      }

      // Set brand
      if (data.brand && typeof data.brand === 'object' && data.brand.id) {
        setBrand({
          id: data.brand.id,
          name: data.brand.name,
          image: data.brand.logo ?? null,
        })
      } else {
        setBrand(null)
      }

      // Set product type (string or null)
      if (data.product_type) {
        setProductType({
          id: data.product_type,
          name: data.product_type,
        })
      } else {
        setProductType(null)
      }

      // Set region
      setRegion(data.region ?? '')
    } else {
      setIsClockedIn(false)
      setClockedInSeconds(0)
      setMember(null)
      setBrand(null)
      setProductType(null)
      setPillar('')
      setRegion('')
    }
  }, [clockStatusQuery.data])

  // Handle clock status errors
  useEffect(() => {
    if (clockStatusQuery.error) {
      console.error('Failed to fetch clock status:', clockStatusQuery.error)
      setIsClockedIn(false)
      setClockedInSeconds(0)
      setMember(null)
      setBrand(null)
      setProductType(null)
      setPillar('')
      setRegion('')
    }
  }, [clockStatusQuery.error])

  const clockInMutation = useClockIn()
  const clockOutMutation = useClockOut()

  const isFormValid = member !== null && pillar !== ''

  const handleClockInOrOut = () => {
    if (!isFormValid) return

    setIsLoadingAction(true)
    const data = {
      member: member!.id,
      pillar,
      ...(brand && { brand: brand.id }),
      ...(productType && { product_type: productType.id }),
      ...(region && { region }),
    }

    if (isClockedIn) {
      clockOutMutation.mutate(data, {
        onSuccess: () => setIsClockedIn(false),
        onError: (error) => console.error('Failed to clock out:', error),
        onSettled: () => setIsLoadingAction(false),
      })
    } else {
      clockInMutation.mutate(data, {
        onSuccess: () => setIsClockedIn(true),
        onError: (error) => console.error('Failed to clock in:', error),
        onSettled: () => setIsLoadingAction(false),
      })
    }
  }

  const formatClockedInTime = (seconds: number) => {
    if (isNaN(seconds) || seconds < 0) {
      return { hours: '00', minutes: '00' }
    }
    const hours = String(Math.floor(seconds / 3600)).padStart(2, '0')
    const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')
    return { hours, minutes }
  }

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null
    if (isClockedIn) {
      timer = setInterval(() => {
        setClockedInSeconds((prevSeconds) => prevSeconds + 1)
      }, 1000)
    } else {
      setClockedInSeconds(0)
    }
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [isClockedIn])

  if (isLoading) {
    return (
      <Flex alignItems="center">
        <Skeleton borderRadius="16px" mr="4px" ml="4" height="32px" width="150px" />
        <SkeletonCircle w="32px" h="32px" />
      </Flex>
    )
  }

  return (
    <Flex alignItems="center" h="40px">
      <ConfirmModal
        isOpen={isConfigModalOpen}
        onClose={onConfigModalClose}
        onConfirm={() => {
          if (!isFormValid) return
          onConfigModalClose()
        }}
        title="Clock In Configuration"
        description="Configure your clock in settings before starting work tracking"
        icon={
          <Icon color="white" width="28px" height="28px" viewBox="0 0 24 24">
            <MusicPlay />
          </Icon>
        }
        confirmButtonText="Continue"
        isLoading={false}
        hideChildrenSpacer
      >
        <Flex flexDirection="column" gap="8px" mt="20px">
          <Box>
            <Text color="neutral.900" fontWeight="400" textStyle="b-sm" mb="8px">
              Member{' '}
              <Text as="span" color="red.500">
                *
              </Text>
            </Text>
            <FormMemberSelect
              value={member}
              onAdd={(item) => setMember(item)}
              onRemove={() => setMember(null)}
              name="member"
              labelOverride=""
            />
          </Box>
          <Box>
            <Text color="neutral.900" fontWeight="400" textStyle="b-sm" mb="8px">
              Pillar{' '}
              <Text as="span" color="red.500">
                *
              </Text>
            </Text>
            <FormSelect
              name="pillar"
              value={pillar}
              onChange={(e) => setPillar(e.target.value)}
              options={PILLAR_TYPES}
              hideLabel
              w="100%"
            />
          </Box>
          <Box>
            <FormBrandSelect
              isDisabled={!member}
              value={brand}
              onAdd={(item) => setBrand(item)}
              onRemove={() => setBrand(null)}
              memberId={member?.id}
              name="brand"
            />
          </Box>
          <Box>
            <FormProductTypeSelect
              value={productType}
              onAdd={(item) => setProductType(item)}
              onRemove={() => setProductType(null)}
              brand={brand?.id}
              name="product_type"
            />
          </Box>
          <Box>
            <FormSelect
              name="region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              options={REGION_TYPES}
              w="100%"
            />
          </Box>
        </Flex>
      </ConfirmModal>

      <Flex
        bg="neutral.50"
        border="1px solid"
        borderColor="neutral.100"
        alignItems="center"
        h="34px"
        borderRadius="16px"
        p="8px 16px 8px 12px"
        gap="4px"
        cursor={isClockedIn ? 'default' : 'pointer'}
        onClick={isClockedIn ? undefined : onConfigModalOpen}
      >
        {member ? (
          <LazyLoadAvatar
            onClick={isClockedIn ? undefined : onConfigModalOpen}
            src={member.image ?? undefined}
            name={member.name}
            w={24}
            h={24}
            isImage
          />
        ) : (
          <AddMemberButton onClick={onConfigModalOpen} />
        )}

        <Divider orientation="vertical" borderColor="neutral.200" m="-8px 4px" h="32px" />
        <Text textStyle="b-sm" color="neutral.500" fontWeight="510">
          {formatDate()}
        </Text>
        <Text textStyle="b-sm" color="neutral.300" fontWeight="510">
          •
        </Text>
        <Text textStyle="b-sm" color="neutral.900" fontWeight="510">
          {currentTime}
        </Text>
      </Flex>

      <Spacer w={isClockedIn ? '8px' : '0'} transition={TRANSITION} />

      <Icon color="neutral.300" w={isClockedIn ? '12px' : '0'} h="12px" viewBox="0 0 12 12" transition={TRANSITION}>
        <ArrowRightThick />
      </Icon>

      <Spacer w={isClockedIn ? '19px' : '0'} transition={TRANSITION} />

      <Flex w={isClockedIn ? '45px' : '0'} transition={TRANSITION} alignItems="center" overflow="hidden">
        {(() => {
          const { hours, minutes } = formatClockedInTime(clockedInSeconds)
          return (
            <>
              <Text as="span" textStyle="b-md" color="neutral.900" fontWeight="510">
                {hours}
              </Text>
              <Text
                as="span"
                mb="2px"
                textStyle="b-md"
                color="neutral.900"
                fontWeight="510"
                animation={`${blinkAnimation} 2s linear infinite`}
              >
                :
              </Text>
              <Text as="span" textStyle="b-md" color="neutral.900" fontWeight="510">
                {minutes}
              </Text>
            </>
          )
        })()}
      </Flex>

      <Spacer w="8px" />

      <Center
        transition={TRANSITION}
        cursor={isFormValid ? 'pointer' : 'not-allowed'}
        onClick={handleClockInOrOut}
        w="34px"
        h="34px"
        borderRadius="full"
        bg={isClockedIn ? 'red.500' : isFormValid ? 'green.500' : 'neutral.300'}
        opacity={isFormValid ? 1 : 0.5}
      >
        {isLoadingAction ? <Spinner size="sm" color="white" /> : isClockedIn ? <StopIcon /> : <PlayIcon />}
      </Center>
    </Flex>
  )
}

export default ClockInClockOut
